/**
 * AchievementsStrip.js — renders a card of earned achievement badges + any
 * course certificates the user holds. Sits on the Dashboard's overlap
 * region so it MUST render as a self-contained light card (dark navy hero
 * bleeds through otherwise).
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Award, GraduationCap, ArrowUpRight, Sparkles, Share2, Copy, Linkedin, Download } from 'lucide-react';
import { toast } from 'sonner';
import BadgeShareKitModal from './BadgeShareKitModal';

const tierBadgeColors = {
  free: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/40',
  tier_1: 'bg-amber-500/10 text-amber-800 border-amber-500/40',
  tier_2: 'bg-primary/10 text-primary border-primary/40',
  tier_3: 'bg-secondary/10 text-secondary border-secondary/40',
};

const AchievementsStrip = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [badges, setBadges] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [shareKit, setShareKit] = useState(null);

  const shareBadge = async (b, medium) => {
    const handle = user?.handle || user?.id || '';
    // Public profile URL — backend serves OG meta tags for LinkedIn/X/Slack
    // unfurl using /api/lms/badge-cards/{id}.png as og:image.
    const url = `${window.location.origin}/u/${handle}?badge=${b.id}`;
    const text = `I just earned the "${b.course_title}" badge on Freeze Dry Academy 🎉`;
    if (medium === 'download') {
      // Direct PNG download from the backend OG card endpoint.
      const png = `${API}/lms/badge-cards/${b.id}.png`;
      const a = document.createElement('a');
      a.href = png;
      a.download = `${(b.course_title || 'badge').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-badge.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Badge PNG downloaded');
      return;
    }
    if (medium === 'copy') {
      try { await navigator.clipboard.writeText(url); toast.success('Link copied'); return; }
      catch { /* fall through to execCommand */ }
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Link copied');
      } catch { toast.error('Copy failed'); }
      return;
    }
    if (medium === 'linkedin') {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        '_blank', 'noopener,noreferrer',
      );
      return;
    }
    if (medium === 'x') {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
        '_blank', 'noopener,noreferrer',
      );
      return;
    }
    if (medium === 'native' && navigator.share) {
      try { await navigator.share({ title: b.course_title, text, url }); }
      catch { /* user cancelled */ }
    }
  };

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const [bRes, cRes] = await Promise.all([
        axios.get(`${API}/lms/my-badges`).catch(() => ({ data: { badges: [] } })),
        axios.get(`${API}/lms/my-certificates`).catch(() => ({ data: { certificates: [] } })),
      ]);
      setBadges(bRes.data?.badges || []);
      setCertificates(cRes.data?.certificates || []);
      setLoaded(true);
    };
    load();
  }, [API, user]);

  if (!loaded) return null;

  const totalItems = badges.length + certificates.length;
  const isEmpty = totalItems === 0;

  return (
    <section
      className="pro-card p-6 sm:p-7 bg-card border border-border rounded-3xl shadow-lg"
      data-testid="achievements-strip"
    >
      <header className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="eyebrow text-primary">Latest achievements</div>
          <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-foreground tracking-tight mt-2">
            {isEmpty ? 'Your trophy shelf is still empty.' : (
              <>
                {totalItems === 1 ? '1 win' : `${totalItems} wins`} to celebrate
              </>
            )}
          </h2>
        </div>
        <Award className="w-8 h-8 text-primary flex-shrink-0" />
      </header>

      {!isEmpty && (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="achievements-quick-actions">
          <button
            onClick={() => navigate('/my-certificates')}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 hover:bg-primary/10 px-3.5 py-1.5 text-xs font-extrabold text-primary transition-colors"
            data-testid="achievements-view-all-btn"
          >
            <GraduationCap className="w-3.5 h-3.5" /> View all certificates
          </button>
          <button
            onClick={() => navigate('/coursework')}
            className="inline-flex items-center gap-1.5 rounded-full border border-border hover:bg-muted px-3.5 py-1.5 text-xs font-extrabold text-foreground transition-colors"
            data-testid="achievements-transcript-btn"
          >
            <Sparkles className="w-3.5 h-3.5" /> Coursework transcript
          </button>
        </div>
      )}

      {isEmpty && (
        <div className="text-sm text-foreground/70 leading-relaxed" data-testid="achievements-empty">
          Finish any Learning course to earn your first certificate — with a unique verifiable ID you can print or share.
          <button
            onClick={() => navigate('/learning')}
            className="ml-1.5 font-bold text-primary hover:underline inline-flex items-center gap-1"
            data-testid="achievements-cta-learning"
          >
            Browse courses <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {certificates.length > 0 && (
        <div className="mb-5" data-testid="achievements-certificates">
          <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60 mb-2.5 flex items-center gap-1.5">
            <GraduationCap className="w-3 h-3" /> Certificates of completion
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {certificates.map((c) => (
              <button
                key={c.cert_number}
                onClick={() => navigate(`/certificate/${c.cert_number}`)}
                className="text-left rounded-2xl border border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 p-3.5 transition-colors group"
                data-testid={`certificate-tile-${c.cert_number}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-sm font-extrabold text-foreground tracking-tight truncate">
                      {c.course_title}
                    </div>
                    <div className="text-[11px] text-foreground/60 mt-0.5 font-mono truncate">
                      {c.cert_number}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-primary flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-foreground/50 font-bold mt-2">
                  Issued {new Date(c.issued_at).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {badges.length > 0 && (
        <div data-testid="achievements-badges">
          <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60 mb-2.5 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Badges earned
          </div>
          <div className="flex flex-wrap gap-2">
            {badges.map((b) => (
              <div
                key={b.id}
                className={`group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${tierBadgeColors[b.course_tier] || tierBadgeColors.free}`}
                data-testid={`badge-${b.course_id}`}
              >
                <Award className="w-3.5 h-3.5" />
                <span className="text-xs font-bold">{b.course_title}</span>
                <span className="text-[9px] uppercase tracking-widest opacity-70">
                  {new Date(b.earned_at).toLocaleDateString()}
                </span>
                {/* Prominent "Share your win" — always visible so nobody
                    has to hover to discover it. Opens the full share kit. */}
                <button
                  type="button"
                  onClick={() => setShareKit(b)}
                  className="ml-1 inline-flex items-center gap-1 rounded-full bg-white/60 dark:bg-white/10 hover:bg-white/80 dark:hover:bg-white/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest transition-colors"
                  title="Open share kit"
                  aria-label="Open share kit"
                  data-testid={`badge-share-kit-open-${b.course_id}`}
                >
                  <Share2 className="w-3 h-3" /> Share
                </button>
                {/* Inline share micro-menu */}
                <div className="flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => shareBadge(b, 'download')}
                    className="p-1 rounded-full hover:bg-white/40 dark:hover:bg-white/10"
                    title="Download badge PNG"
                    aria-label="Download badge PNG"
                    data-testid={`badge-share-download-${b.course_id}`}
                  >
                    <Download className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shareBadge(b, 'copy')}
                    className="p-1 rounded-full hover:bg-white/40 dark:hover:bg-white/10"
                    title="Copy shareable link"
                    aria-label="Copy shareable link"
                    data-testid={`badge-share-copy-${b.course_id}`}
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shareBadge(b, 'linkedin')}
                    className="p-1 rounded-full hover:bg-white/40 dark:hover:bg-white/10"
                    title="Share to LinkedIn"
                    aria-label="Share to LinkedIn"
                    data-testid={`badge-share-linkedin-${b.course_id}`}
                  >
                    <Linkedin className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shareBadge(b, navigator.share ? 'native' : 'x')}
                    className="p-1 rounded-full hover:bg-white/40 dark:hover:bg-white/10"
                    title="Share…"
                    aria-label="Share"
                    data-testid={`badge-share-more-${b.course_id}`}
                  >
                    <Share2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <BadgeShareKitModal
        open={!!shareKit}
        onClose={() => setShareKit(null)}
        badge={shareKit}
      />
    </section>
  );
};

export default AchievementsStrip;
